import { gql } from "@apollo/client";
import { Form, Formik, FormikHelpers } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import {
  campaignEditConfigurationForNumberProvisioning,
  campaignEditConfigurationForNumberProvisioningVariables
} from "src/graphql/generated/campaignEditConfigurationForNumberProvisioning";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { areaCodeHelperText } from "src/util/areaCodeHelperText";
import { graphqlErrorHasCode } from "src/util/errors";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { FormikSaveButtons } from "./SaveButtons";

type Values = {
  areaCode: string;
  goToNext?: boolean;
};

const schemaForCountry = (countryIso: string) =>
  yup.object().shape({
    areaCode: yup.string().required("Required").areaCode(countryIso)
  });

const EDIT_CAMPAIGN_NUMBERS = gql`
  mutation campaignEditConfigurationForNumberProvisioning(
    $id: ID!
    $areaCode: String!
  ) {
    campaignEditConfigurationForNumberProvisioning(
      id: $id
      areaCode: $areaCode
    ) {
      id
      phone_number_config {
        outbound {
          areaCode
        }
      }
      outboundPhoneNumbers {
        id
        phoneNumber
        rotationErrors {
          message
        }
      }
    }
  }
`;

export default function Numbers({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useTenant()!;

  const [editCampaignNumbers] = useMutation<
    campaignEditConfigurationForNumberProvisioning,
    campaignEditConfigurationForNumberProvisioningVariables
  >(EDIT_CAMPAIGN_NUMBERS);

  const sendEditEvent = useCampaignEditEvents();

  const handleSave = async (
    { areaCode, goToNext }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const { errors, data } = await editCampaignNumbers({
      variables: {
        id: campaign.id,
        areaCode
      }
    });
    setSubmitting(false);
    if (errors) {
      if (
        graphqlErrorHasCode("NO_PHONE_NUMBERS_IN_DESIRED_AREA_CODE", errors) ||
        graphqlErrorHasCode(
          "UNABLE_TO_EDIT_AREA_CODE_DURING_CAMPAIGN_START",
          errors
        )
      ) {
        enqueueSnackbar(errors[0].message, { variant: "error" });
        resetForm({
          values: {
            areaCode: campaign.phone_number_config?.outbound.areaCode || ""
          }
        });
      } else {
        enqueueSnackbar("Could not edit campaign", { variant: "error" });
      }
    } else {
      sendEditEvent();
      resetForm({
        values: {
          areaCode:
            data!.campaignEditConfigurationForNumberProvisioning
              ?.phone_number_config!.outbound!.areaCode || ""
        }
      });
      if (goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  const preventEditing =
    !!campaign.startJob &&
    !["DONE", "FAILED"].includes(campaign.startJob.status);

  return (
    <Formik
      initialValues={{
        areaCode: campaign.phone_number_config?.outbound.areaCode || ""
      }}
      onSubmit={handleSave}
      validationSchema={schemaForCountry(tenant.countryIso)}
    >
      {props => (
        <Form>
          <RouteLeavingGuard when={props.dirty} />
          <h2>Choose Caller ID</h2>
          <FormikErrorBanner />
          <FormikTextField
            label={`Caller ID area code${
              preventEditing ? " (disabled while the campaign is starting)" : ""
            }`}
            name="areaCode"
            helperText={areaCodeHelperText(tenant.countryIso)}
            disabled={preventEditing}
          />
          <FormikSaveButtons
            disabled={preventEditing}
            continueWithoutSaving={goToNextSection}
          />
        </Form>
      )}
    </Formik>
  );
}
